import React, { useEffect } from 'react';
import { Box, Flex, Icon, Image, Text, VStack, HStack, useDisclosure } from '@chakra-ui/react';
import { FiCheck } from 'react-icons/fi';
import { ShopifyProduct } from '../../../../../types/shopify';
import { ProductModal } from '../../shopify/ProductModal';
import { getProductMainImage } from '../../../../../utils/shopifyUtils';
import { ff } from '../../../../../theme';

interface SelectionCardProps {
  product: ShopifyProduct;
  availableVariants: string[];
  selectedVariant: string | null;
  onVariantSelect: (variantId: string) => void;
}

const SelectionCard: React.FC<SelectionCardProps> = ({
  product,
  availableVariants,
  selectedVariant,
  onVariantSelect,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const filteredVariants = product.variants.filter(variant => availableVariants.includes(variant.id));
  const isSingleVariant = filteredVariants.length === 1;
  const mainImage = getProductMainImage(product);

  useEffect(() => {
    if (isSingleVariant && filteredVariants[0]) {
      onVariantSelect(filteredVariants[0].id);
    }
  }, []);

  const handleVariantSelect = (variantId: string) => {
    onVariantSelect(variantId);
  };

  return (
    <>
      <Box
        borderWidth="1px"
        borderRadius="20px"
        overflow="hidden"
        borderColor={ff.line}
        bg="white"
        _hover={{
          borderColor: ff.border,
          transition: "all 0.2s ease-in-out",
          cursor: "pointer"
        }}
        onClick={onOpen}
      >
        <HStack spacing={4} p={4} align="start">
          <Box
            width="96px"
            height="96px"
            flexShrink={0}
            borderRadius="20px"
            overflow="hidden"
            bg={ff.grey}
          >
            {mainImage && (
              <Image
                src={mainImage.url}
                alt={mainImage.altText}
                objectFit="cover"
                width="100%"
                height="100%"
              />
            )}
          </Box>
          <VStack align="stretch" flex={1} minW={0} spacing={3}>
            <Box>
              <Text fontSize="15px" fontWeight="600" color={ff.ink}>
                {product.title}
              </Text>
              {isSingleVariant && (
                <Text fontSize="14px" color={ff.text2} mt={0.5}>
                  ${filteredVariants[0].price} value
                </Text>
              )}
              <Text fontSize="13px" color={ff.text2} noOfLines={2} mt={1}>
                {product.description}
              </Text>
            </Box>
          </VStack>
        </HStack>

        {!isSingleVariant && (
          <VStack align="stretch" spacing={2} px={4} pb={4}>
            <Text textStyle="eyebrow" color={ff.text2}>
              Select an option
            </Text>
            {filteredVariants.map(variant => {
              const isSelected = selectedVariant === variant.id;
              return (
                <Flex
                  as="button"
                  type="button"
                  key={variant.id}
                  w="100%"
                  minH="52px"
                  px={4}
                  py={2}
                  gap={3}
                  align="center"
                  textAlign="left"
                  borderWidth="1px"
                  borderRadius="16px"
                  borderColor={isSelected ? ff.ink : ff.line}
                  bg={isSelected ? ff.limeSurface : 'white'}
                  _hover={isSelected ? undefined : { borderColor: ff.border }}
                  transition="all 0.2s"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleVariantSelect(variant.id);
                  }}
                >
                  <Flex
                    w="22px"
                    h="22px"
                    borderRadius="full"
                    flexShrink={0}
                    align="center"
                    justify="center"
                    bg={isSelected ? ff.lime : 'white'}
                    border={isSelected ? 'none' : `1.5px solid ${ff.border}`}
                  >
                    {isSelected && <Icon as={FiCheck} boxSize="14px" color={ff.ink} />}
                  </Flex>
                  <Text flex={1} fontSize="14px" fontWeight={isSelected ? 600 : 400} color={ff.ink}>
                    {variant?.selectedOptions?.map(opt => `${opt.value}`).join(' / ')}
                  </Text>
                  <Text fontSize="13px" color={ff.text2} flexShrink={0}>
                    ${variant.price} value
                  </Text>
                </Flex>
              );
            })}
          </VStack>
        )}
      </Box>

      <ProductModal
        isOpen={isOpen}
        onClose={onClose}
        product={product}
        selectedVariant={selectedVariant ? product.variants.find(v => v.id === selectedVariant) || null : null}
        productVariants={availableVariants}
      />
    </>
  );
};

export default SelectionCard;